import React from 'react';
import { PROJECTS } from '../projects.config';

function StatusBadge({ status, C }) {
	if (status === 'active') {
		return (
			<span
				style={{
					fontFamily: 'DM Mono',
					fontSize: 9,
					letterSpacing: '0.12em',
					textTransform: 'uppercase',
					color: '#6DBF67',
					border: '1px solid #6DBF6740',
					padding: '3px 8px',
				}}
			>
				Active Research
			</span>
		);
	}
	return (
		<span
			style={{
				fontFamily: 'DM Mono',
				fontSize: 9,
				letterSpacing: '0.12em',
				textTransform: 'uppercase',
				color: '#4A6A66',
				border: '1px solid #1A2E2A',
				padding: '3px 8px',
			}}
		>
			Research In Progress
		</span>
	);
}

function ProjectCard({ project, C, Link, compact, isLast }) {
	const isActive = project.status === 'active';

	return (
		<div
			style={{
				borderBottom: isLast ? 'none' : '1px solid #1A2E2A',
				paddingBottom: isLast ? 0 : compact ? 48 : 64,
				marginBottom: isLast ? 0 : compact ? 48 : 64,
				opacity: isActive ? 1 : 0.72,
			}}
		>
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: compact ? '1fr' : '280px 1fr',
					gap: compact ? 28 : 64,
					alignItems: 'flex-start',
				}}
			>
				{/* Left column — identity */}
				<div>
					<div
						style={{
							fontFamily: 'DM Mono',
							fontSize: 10,
							letterSpacing: '0.18em',
							textTransform: 'uppercase',
							color: isActive ? C.tealLight : '#4A6A66',
							marginBottom: 10,
						}}
					>
						{project.dates}
					</div>
					<h2
						style={{
							fontFamily: 'Playfair Display',
							fontSize: compact ? 26 : 32,
							color: isActive ? 'white' : '#8AACAA',
							lineHeight: 1.2,
							marginBottom: 8,
						}}
					>
						{project.name}
					</h2>
					<div
						style={{
							fontFamily: 'DM Mono',
							fontSize: 11,
							color: '#4A6A66',
							letterSpacing: '0.06em',
							marginBottom: 16,
						}}
					>
						{project.neighborhood} · {project.location}
					</div>
					<StatusBadge status={project.status} C={C} />

					{/* Stats for active projects */}
					{isActive && project.stats && (
						<div
							style={{
								display: 'flex',
								flexDirection: compact ? 'row' : 'column',
								gap: compact ? 28 : 20,
								marginTop: 32,
								flexWrap: 'wrap',
							}}
						>
							{project.stats.map(({ n, l }) => (
								<div key={l}>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: compact ? 22 : 28,
											color: C.tealLight,
											fontWeight: 500,
											lineHeight: 1,
											marginBottom: 4,
										}}
									>
										{n}
									</div>
									<div
										style={{
											fontFamily: 'DM Mono',
											fontSize: 9,
											letterSpacing: '0.12em',
											textTransform: 'uppercase',
											color: '#4A6A66',
										}}
									>
										{l}
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Right column — content */}
				<div>
					<p
						style={{
							fontSize: 15,
							color: isActive ? '#8AACAA' : '#6A8A86',
							lineHeight: 1.9,
							marginBottom: 20,
							maxWidth: 640,
						}}
					>
						{project.summary}
					</p>
					<p
						style={{
							fontSize: 13,
							color: '#4A6A66',
							lineHeight: 1.9,
							maxWidth: 580,
							marginBottom: 28,
						}}
					>
						{project.context}
					</p>

					{isActive ? (
						<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
							<Link to={project.route} className='btn-primary'>
								View Project →
							</Link>
							{project.dataLinks?.map(({ to, label }) => (
								<Link key={to} to={to} className='btn-outline'>
									{label}
								</Link>
							))}
						</div>
					) : (
						<Link
							to={project.route}
							style={{
								fontFamily: 'DM Mono',
								fontSize: 11,
								letterSpacing: '0.12em',
								textTransform: 'uppercase',
								color: '#4A6A66',
								textDecoration: 'none',
								display: 'inline-flex',
								alignItems: 'center',
								gap: 8,
								borderBottom: '1px solid #1A2E2A',
								paddingBottom: 4,
								transition: 'color 0.2s, border-color 0.2s',
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.color = C.tealLight;
								e.currentTarget.style.borderColor = C.tealLight;
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.color = '#4A6A66';
								e.currentTarget.style.borderColor = '#1A2E2A';
							}}
						>
							Read Research Context →
						</Link>
					)}
				</div>
			</div>
		</div>
	);
}

function ProjectsHubPage({ C, Link, useBreakpoint }) {
	const { isMobile, isTablet, px } = useBreakpoint();
	const compact = isMobile || isTablet;

	return (
		<div style={{ paddingTop: 100 }}>
			{/* Header */}
			<div style={{ padding: `60px ${px} 48px` }}>
				<div className='section-label fade-up' style={{ marginBottom: 20 }}>
					Center for the Uncounted
				</div>
				<h1
					className='display-heading fade-up delay-1'
					style={{
						fontSize: compact ? 'clamp(30px,7vw,52px)' : 'clamp(38px,5vw,64px)',
						maxWidth: 680,
						marginBottom: 28,
					}}
				>
					Research Projects
					<br />
					<span style={{ color: C.teal }}>Counting the uncounted.</span>
				</h1>
				<div
					style={{ width: 56, height: 3, background: C.teal, marginBottom: 28 }}
				/>
				<p
					style={{
						fontSize: compact ? 15 : 17,
						color: '#8AACAA',
						maxWidth: 620,
						lineHeight: 1.9,
					}}
				>
					CDM applies the same probabilistic framework across distinct historical
					events — each a mass atrocity, each poorly documented, each deserving a
					full accounting of those who were lost.
				</p>
			</div>

			{/* Project list */}
			<div
				style={{
					padding: `60px ${px} 80px`,
					borderTop: '1px solid #1A2E2A',
				}}
			>
				{PROJECTS.map((project, i) => (
					<ProjectCard
						key={project.id}
						project={project}
						C={C}
						Link={Link}
						compact={compact}
						isLast={i === PROJECTS.length - 1}
					/>
				))}
			</div>

			{/* Methodology callout */}
			<div
				style={{
					background: '#070F0D',
					padding: `48px ${px}`,
					display: 'flex',
					flexDirection: compact ? 'column' : 'row',
					justifyContent: 'space-between',
					alignItems: compact ? 'flex-start' : 'center',
					gap: 32,
				}}
			>
				<div>
					<div
						style={{
							fontFamily: 'DM Mono',
							fontSize: 10,
							letterSpacing: '0.18em',
							textTransform: 'uppercase',
							color: C.tealLight,
							marginBottom: 12,
						}}
					>
						Shared Methodology
					</div>
					<p
						style={{
							fontSize: compact ? 15 : 17,
							color: '#8AACAA',
							maxWidth: 520,
							lineHeight: 1.7,
						}}
					>
						Every project uses the same census disruption framework — adapted to
						each event's geography, timeline, and available records.
					</p>
				</div>
				<Link
					to='/methodology'
					className='btn-outline'
					style={{ flexShrink: 0 }}
				>
					Read the Methodology →
				</Link>
			</div>
		</div>
	);
}

export default ProjectsHubPage;
